import { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2 } from 'lucide-react';
import { useSoundtrackState } from '@/lib/audio/use-soundtrack';
import { cn } from '@/lib/utils';

interface CombatHighlightVideoProps {
  src: string;
  className?: string;
  onReady?: () => void;
}

/**
 * Single-pass combat clip with optional impact audio.
 *
 * Browsers may reject unmuted autoplay even after gameplay interaction. In
 * that case the clip still starts muted and exposes a one-tap sound affordance.
 * The app's global audio switch and volume remain authoritative.
 */
export default function CombatHighlightVideo({
  src,
  className,
  onReady,
}: CombatHighlightVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const readySrcRef = useRef<string | null>(null);
  const pendingReleaseRef = useRef<number | null>(null);
  const { audioOn, volume } = useSoundtrackState();
  const [needsSoundTap, setNeedsSoundTap] = useState(false);
  const audible = audioOn && volume > 0;

  // Release the media resource on unmount. pause() alone keeps the demuxer,
  // decoder, and frame buffers alive on the detached element (native memory
  // the JS GC never sees), so every highlight bubble that unmounts (message
  // cap eviction, panel/route swap, session reset) would leak a decoder and
  // walk back toward the SIGILL renderer crash ManagedVideo was built to fix.
  // The release is deferred one tick and gated on the element being out of
  // the DOM, so StrictMode's dev remount (cleanup on a still-connected
  // element, then re-mount without re-stamping the unchanged src) is a no-op.
  useEffect(() => {
    if (pendingReleaseRef.current !== null) {
      window.clearTimeout(pendingReleaseRef.current);
      pendingReleaseRef.current = null;
    }
    const video = videoRef.current;
    return () => {
      pendingReleaseRef.current = window.setTimeout(() => {
        pendingReleaseRef.current = null;
        if (video && !video.isConnected) {
          video.pause();
          video.removeAttribute('src');
          video.load();
        }
      }, 0);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
    if (!audible) {
      video.muted = true;
      setNeedsSoundTap(false);
    }
  }, [audible, volume]);

  const tryPlay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
    video.muted = !audible;
    try {
      await video.play();
      setNeedsSoundTap(false);
    } catch {
      if (!audible) return;
      video.muted = true;
      try {
        await video.play();
        setNeedsSoundTap(true);
      } catch {
        // Native controls remain available when autoplay is fully blocked.
      }
    }
  }, [audible, volume]);

  const enableSound = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
    video.muted = false;
    setNeedsSoundTap(false);
    void video.play();
  }, [volume]);

  const handleCanPlay = useCallback(() => {
    void tryPlay();
    if (readySrcRef.current === src) return;
    readySrcRef.current = src;
    onReady?.();
  }, [onReady, src, tryPlay]);

  return (
    <div className="relative h-full w-full bg-black">
      <video
        ref={videoRef}
        src={src}
        autoPlay
        playsInline
        controls
        preload="metadata"
        muted={!audible}
        onCanPlay={handleCanPlay}
        className={cn('h-full w-full object-cover', className)}
      />
      {needsSoundTap && (
        <button
          type="button"
          onClick={enableSound}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-black/75 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm"
          aria-label="Play combat highlight with sound"
        >
          <Volume2 className="h-3.5 w-3.5" />
          Tap for sound
        </button>
      )}
    </div>
  );
}
